import { useEffect, useMemo, useState, ReactNode } from 'react';
import Tabs from 'src/components/Tabs';
import Skeleton from 'src/components/Skeleton/Skeleton';
import SuiteTabSkeleton from 'src/pages/Suite/Bottom/skeletons/SuiteTabSkeleton';
import SuiteAssetsSkeleton from 'src/pages/Suite/Bottom/skeletons/SuiteAssetsSkeleton';
import { variables } from 'src/Tenants';
import SuiteWalkThroughs from 'src/pages/Suite/SuiteWalkThroughs';
import {
  ISparkDeposit,
  IContractFile,
} from '../../../../public/APIContracts/APIContracts';
import { getSparkDeposits } from 'src/services/client';
import SuiteNotes from 'src/pages/Suite/SuiteNotes/SuiteNotes';
import { UseNotesAPIReturn } from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import {
  fetchContractFiles,
  getDynamicMessagesLabel,
  getFilteredTabsForHomeOwner,
} from 'src/pages/Suite/Bottom/helpers';
import { BaseTabItem, TabItem } from 'src/pages/Suite/Bottom/types';
import {
  useAdminTabItems,
  UseAdminTabItemsReturn,
  useBaseTabItems,
  UseBaseTabItemsReturn,
  useStrataManagerTabItems,
  UseStrataManagerTabItemsReturn,
} from 'src/pages/Suite/Bottom/CustomHooks/SuiteTabsHooks';

interface SuiteTabNavigationProps {
  walkThroughs: any[];
  userRole: string;
  activeTabIndex: number;
  isHomeOwnerOccupantUser: boolean;
  suite: any;
  property: any;
  propertyManagerDetails: any;
  history: any;
  counts: any;
  branding: any;
  match: any;
  isEventShow: boolean;
  noteVisibleToHomeownerOccupantRealtor: boolean;
  notesAPI: UseNotesAPIReturn;
  onFetchSuiteData: () => void;
  refreshSuiteCounts: () => Promise<void>;
  properties: any;
  allowedTabList: any;
  loading?: boolean;
}

const SuiteTabs = ({
  walkThroughs,
  userRole,
  activeTabIndex,
  isHomeOwnerOccupantUser,
  suite: rawSuite,
  property,
  propertyManagerDetails,
  history,
  counts: rawCounts,
  branding,
  match,
  isEventShow,
  noteVisibleToHomeownerOccupantRealtor,
  notesAPI,
  onFetchSuiteData,
  refreshSuiteCounts,
  properties,
  allowedTabList,
  loading,
}: SuiteTabNavigationProps) => {
  // Stub suite/counts during the parent's initial fetch so the tab-building
  // hooks below can run unconditionally — keeps Rules of Hooks intact and
  // lets the placeholder share label/order with the real tabs.
  const suite: any = rawSuite || {};
  const counts: any = rawCounts || {};

  const [sparkDeposits, setSparkDeposits] = useState<ISparkDeposit[]>();
  const [sparkDepositsCount, setSparkDepositsCount] = useState<number>(0);
  const [contractFiles, setContractFiles] = useState<IContractFile[]>();
  const [sparkContractsCount, setSparkContractsCount] = useState<number>(0);

  const recordSparkDepositsCount = (newDepositCount: number) => {
    setSparkDepositsCount(newDepositCount);
  };

  const recordSparkContractsCount = (newContractsCount: number) => {
    setSparkContractsCount(newContractsCount);
  };

  const calculateAggregatedDocCount = (
    sparkContractsCount: number,
    documentsCount: number
  ) => {
    const totalCount: number = sparkContractsCount + documentsCount;
    return totalCount.toString();
  };

  // Fetch contract files in parent component.
  useEffect(() => {
    if (!suite?.id) return;
    fetchContractFiles(
      recordSparkContractsCount,
      setContractFiles,
      suite,
      property
    );
  }, [suite?.id, suite?.isSpark, property?.isSpark]);

  useEffect(() => {
    const fetchSparkDeposits = async () => {
      if (!property?.isSpark || !suite?.isSpark || !suite?.id) {
        return;
      }

      try {
        const results: ISparkDeposit[] = await getSparkDeposits(suite.id);

        if (results.length > 0) {
          recordSparkDepositsCount(results.length);
          setSparkDeposits(results);
        }
      } catch (error) {
        console.error(
          '💥 Unexpected exception encountered while fetching spark deposits:',
          error
        );
      }
    };

    fetchSparkDeposits();
  }, [suite?.id, suite?.isSpark, property?.isSpark]);

  const messagesLabel: string = useMemo(
    () => getDynamicMessagesLabel(allowedTabList),
    [allowedTabList]
  );

  const tabConfig = {
    suite,
    property,
    history,
    counts,
    branding,
    match,
    properties,
    contractFiles,
    sparkContractsCount,
    sparkDeposits,
    sparkDepositsCount,
    messagesLabel,
    notesAPI,
    onFetchSuiteData,
    refreshSuiteCounts,
    calculateAggregatedDocCount,
    isEventShow,
    propertyManagerDetails,
  };

  const baseTabItems: UseBaseTabItemsReturn = useBaseTabItems(tabConfig);
  const adminTabItems: UseAdminTabItemsReturn = useAdminTabItems(tabConfig);
  const strataManagerTabItems: UseStrataManagerTabItemsReturn =
    useStrataManagerTabItems(tabConfig);

  const notesTab: TabItem = useMemo(() => {
    const shouldShowNotes: boolean = !(
      isHomeOwnerOccupantUser && !noteVisibleToHomeownerOccupantRealtor
    );

    const newTabItem: TabItem = {
      label: 'Notes',
      count: counts.notesCount?.toString() || '0',
      content: (
        <SuiteNotes
          suite={suite}
          notesAPI={notesAPI}
          onFetchSuiteData={onFetchSuiteData}
        />
      ),
    };

    return shouldShowNotes ? newTabItem : null;
  }, [
    isHomeOwnerOccupantUser,
    noteVisibleToHomeownerOccupantRealtor,
    counts,
    suite,
  ]);

  // Memoized walkthrough tab.
  const walkThroughTab: BaseTabItem = useMemo(() => {
    const newWalkThroughTab: BaseTabItem = {
      label: 'WalkThrough',
      count: counts.walkthroughsCount?.toString() || '0',
      content: (
        <SuiteWalkThroughs
          suite={suite}
          property={property}
          isEventShow={isEventShow}
          propertyManagerDetails={propertyManagerDetails}
          refreshSuiteCounts={refreshSuiteCounts}
        />
      ),
    };

    return newWalkThroughTab;
  }, [suite, property, isEventShow, counts, refreshSuiteCounts]);

  // Check for active walkthroughs.
  const hasActiveWalkthroughs: boolean = useMemo(() => {
    return (
      walkThroughs.some((walkThroughItem: any) => {
        if (walkThroughItem.walkthroughType === 'Home Owner Walk-through') {
          const activeStatuses = [
            'Guided Walk Through',
            'Self-Guided Walk Through',
            'In Progress Home Only',
            'In Progress Home',
            'In Progress',
            'Begin',
            'Schedule',
          ];

          return activeStatuses.includes(walkThroughItem.status);
        }

        return false;
      }) && walkThroughs.length > 0
    );
  }, [walkThroughs]);

  // Memoized occupant tabs.
  const occupantTabs: BaseTabItem[] = useMemo(() => {
    if (hasActiveWalkthroughs) {
      return [walkThroughTab, ...baseTabItems, ...(notesTab ? [notesTab] : [])];
    }

    return [...baseTabItems, ...(notesTab ? [notesTab] : []), walkThroughTab];
  }, [baseTabItems, notesTab, walkThroughTab, hasActiveWalkthroughs]);

  const defaultTabs: BaseTabItem[] = useMemo(() => {
    return [...baseTabItems, ...(notesTab ? [notesTab] : [])];
  }, [baseTabItems, notesTab]);

  const tabItems: BaseTabItem[] = useMemo(() => {
    const OCCUPANT_ROLES: string[] = ['Home Owner', 'Realtor', 'Occupant'];

    const ADMIN_ROLES: string[] = [
      'Property Developer',
      'Admin',
      'Super Admin',
    ];

    if (OCCUPANT_ROLES.includes(userRole)) {
      /* legacy/workaround: the BOS modules endpoint doesn't return a "Notes"
       * entry, so getFilteredTabsForHomeOwner strips notesTab on a label match.
       * Re-append it here when an occupant-visible note exists.
       * TODO: Fix this to be handled API side properly. */
      const filteredTabs: BaseTabItem[] = getFilteredTabsForHomeOwner(
        occupantTabs,
        allowedTabList
      );

      return notesTab ? [...filteredTabs, notesTab] : filteredTabs;
    }

    if (ADMIN_ROLES.includes(userRole)) {
      return adminTabItems;
    }

    if (userRole === 'Strata Manager') {
      return strataManagerTabItems;
    }

    return defaultTabs;
  }, [
    userRole,
    occupantTabs,
    adminTabItems,
    strataManagerTabItems,
    defaultTabs,
    notesTab,
    allowedTabList,
  ]);

  // While the parent is fetching, keep the real tab schema (labels/order) but
  // swap each tab's count badge and content for skeletons. Labels stay locked
  // to whatever useBaseTabItems/useAdminTabItems produce, so they can't drift.
  const displayItems: any[] = useMemo(() => {
    if (!loading) return tabItems;

    return tabItems.map((tab: BaseTabItem) => ({
      ...tab,
      count: <Skeleton variant="text" width="1.4rem" />,
      content: getSkeletonForTab(tab.label),
    }));
  }, [loading, tabItems]);

  return <Tabs from="suite" activeTab={activeTabIndex} items={displayItems} />;
};

// Per-tab loading-state body. Rooms & Items lands first by default and gets
// a skeleton matching AreaAssetsTable's chrome; deep-link landings on other
// tabs fall back to the generic block skeleton.
const getSkeletonForTab = (label: string): ReactNode => {
  if (label === variables.roomsAndItems) {
    return <SuiteAssetsSkeleton />;
  }
  return <SuiteTabSkeleton />;
};

export default SuiteTabs;
